
import { defineComponent, PropType } from 'vue';
import { mapGetters } from 'vuex';
import { hexToRgba } from '@/utils';

export default defineComponent({
  name: 'Select',
  props: {
    modelValue: {
      type: String,
      required: true
    },
    id: {
      type: String,
      default: ''
    },
    defaultSelectorText: {
      type: String,
      required: true
    },
    defaultSelectorAriaLabel: {
      type: String,
      default: ''
    },
    options: {
      type: Array as PropType<Array<string>>,
      required: true
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    valid: {
      type: Boolean,
      default: false
    },
    defaultSelectorValue: {
      type: String,
      default: undefined,
      required: false
    }
  },
  computed: {
    ...mapGetters([
      'global'
    ]),
    hasRequiredAttribute (): boolean {
      return this.$attrs.required !== undefined ||
        this.$attrs['aria-required'] === true ||
        this.$attrs['aria-required'] === 'true';
    },
    boxColor (): string {
      return this.global.boxColor || '#696E79';
    },
    successColor (): string {
      return this.global.successColor || '#337E33';
    },
    hasValue (): boolean {
      return !!this.modelValue && this.modelValue !== this.defaultSelectorText;
    },
    defaultBorderColor (): string {
      return this.hasValue
        ? this.boxColor
        : hexToRgba(this.boxColor, 0.5);
    },
    inputDescribedBy (): string | undefined {
      const ids: string[] = [];
      const describedBy = this.$attrs['aria-describedby'];

      if (typeof describedBy === 'string' && describedBy.trim()) {
        ids.push(describedBy.trim());
      }

      if (this.statusAnnouncement && this.statusMessageId) {
        ids.push(this.statusMessageId);
      }

      if (this.error && this.errorMessage && this.id) {
        ids.push(`${this.id}-error`);
      }

      return ids.length ? ids.join(' ') : undefined;
    },
    fieldLabelText (): string {
      const ariaLabel = this.$attrs['aria-label'];

      if (typeof ariaLabel === 'string' && ariaLabel.trim()) {
        return ariaLabel.trim();
      }

      return this.defaultSelectorText.replace(/\*/g, '').trim();
    },
    statusAnnouncement (): string {
      if (!this.valid || !this.modelValue || this.error || !this.fieldLabelText) {
        return '';
      }

      return this.$t('message.accessibility.field_valid', {
        field: this.fieldLabelText
      }) as string;
    },
    statusMessageId (): string | undefined {
      return this.id ? `${this.id}-status` : undefined;
    },
    visibleErrorMessage (): string {
      if (!this.errorMessage) {
        return '';
      }

      return this.errorMessage.startsWith('* ')
        ? this.errorMessage
        : `* ${this.errorMessage}`;
    }
  },
  methods:{
    handleFirstClick(){
      if(!this.defaultSelectorValue)
        return;
      if(this.modelValue == this.defaultSelectorText){
        this.$emit('update:modelValue', this.defaultSelectorValue);
      }
    }
  }
});
